import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../services/apiClient';
import { useAuth } from './AuthContext';
import { FeatureName } from '../types/featureNames';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

interface FeatureFlagContextType {
    /** List of active feature names for the current user's tenant + role. */
    features: string[];
    /** Returns true if the given feature is enabled for the current user. */
    hasFeature: (feature: FeatureName) => boolean;
    /** True while the initial fetch is in progress. */
    isLoading: boolean;
}

// --------------------------------------------------------------------------
// Context
// --------------------------------------------------------------------------

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
    }
    return context;
};

// --------------------------------------------------------------------------
// Provider
// --------------------------------------------------------------------------

interface FeatureFlagProviderProps {
    children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [features, setFeatures] = useState<string[]>([]);
    let [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            setFeatures([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        apiClient
            .get('/api/v1/auth/features')
            .then((res) => {
                if (!cancelled) {
                    setFeatures(res.data?.features ?? []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFeatures([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const hasFeature = useCallback(
        (feature: FeatureName): boolean => {
            return features.includes(feature as string);
        },
        [features],
    );

    const value: FeatureFlagContextType = {
        features,
        hasFeature,
        isLoading,
    };

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export default FeatureFlagContext;
